import { useState, useCallback } from 'react';
import { MdAdd, MdFolder, MdExpandMore, MdChevronRight, MdInsertDriveFile, MdArchive, MdUnarchive } from 'react-icons/md';
import {
  DndContext,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Sortable Item Component for drag and drop
const SortableItem = ({ id, children, type }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, data: { type } });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      {children}
    </div>
  );
};

const FileExplorer = ({
  files,
  projects,
  activeFile,
  loading,
  onFileSelect,
  onShowFileModal,
  onShowProjectModal,
  onMoveFileToProject,
  onRefresh,
}) => {
  const [expandedProjects, setExpandedProjects] = useState(new Set());
  const [showArchived, setShowArchived] = useState(false);
  const [contextMenu, setContextMenu] = useState({
    isOpen: false,
    position: { x: 0, y: 0 },
    type: null, // 'root', 'project', or 'file'
    target: null, // project object, file object, or null
    isArchived: false  // Add this to track if item is archived
  });

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Root level droppable zone for standalone files
  const { setNodeRef: setRootDropRef, isOver: isRootOver } = useDroppable({
    id: 'root-drop-zone',
    data: { type: 'root', project_id: null }
  });

  const toggleProject = useCallback((projectId) => {
    const newExpanded = new Set(expandedProjects);
    if (newExpanded.has(projectId)) {
      newExpanded.delete(projectId);
    } else {
      newExpanded.add(projectId);
    }
    setExpandedProjects(newExpanded);
  }, [expandedProjects]);

  const handleArchiveFile = async (fileId) => {
    try {
      const { codeEditorApi } = await import('../../utils/api/CodeEditorApi');
      const response = await codeEditorApi.archiveFile(fileId, { class_id: files[0]?.class_id || projects[0]?.class_id });
      if (response.success && onRefresh) {
        onRefresh();
      }
    } catch (error) {
      console.error('Failed to archive file:', error);
    }
  };

  const handleUnarchiveFile = async (fileId) => {
    try {
      const { codeEditorApi } = await import('../../utils/api/CodeEditorApi');
      const response = await codeEditorApi.unarchiveFile(fileId, { class_id: files[0]?.class_id || projects[0]?.class_id });
      if (response.success && onRefresh) {
        onRefresh();
      }
    } catch (error) {
      console.error('Failed to unarchive file:', error);
    }
  };

  const handleArchiveProject = async (projectId) => {
    try {
      const { codeEditorApi } = await import('../../utils/api/CodeEditorApi');
      const response = await codeEditorApi.archiveProject(projectId, true, { class_id: projects[0]?.class_id });
      if (response.success && onRefresh) {
        onRefresh();
      }
    } catch (error) {
      console.error('Failed to archive project:', error);
    }
  };

  const handleUnarchiveProject = async (projectId) => {
    try {
      const { codeEditorApi } = await import('../../utils/api/CodeEditorApi');
      const response = await codeEditorApi.unarchiveProject(projectId, true, { class_id: projects[0]?.class_id });
      if (response.success && onRefresh) {
        onRefresh();
      }
    } catch (error) {
      console.error('Failed to unarchive project:', error);
    }
  };

  const getFileIcon = () => {
    const iconClass = 'text-blue-500';
    return <MdInsertDriveFile className={iconClass} size={16} />;
  };

  const renderFile = (fileItem, isIndented = false) => {
    const fileName = fileItem?.file?.name || fileItem?.name || 'Unknown File';
    const isActive = activeFile?.file?.file_id === fileItem?.file?.file_id;
    const isArchived = fileItem?.archived || false;

    return (
      <SortableItem key={fileItem.file.file_id} id={fileItem.file.file_id} type="file">
        <div
          className={`flex items-center py-1.5 px-3 hover:bg-gray-50/80 transition-colors duration-200 rounded-lg mx-2 cursor-pointer ${isActive ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
            } ${isIndented ? 'ml-6' : ''} ${isArchived ? 'opacity-50' : ''}`}
          onClick={() => onFileSelect(fileItem)}
          onContextMenu={(e) => handleContextMenu(e, 'file', fileItem, isArchived)}
        >
          <div className="w-4 h-4 mr-2 flex-shrink-0" /> {/* Spacer for alignment */}
          {getFileIcon()}
          <div className="mr-2" /> {/* Small spacer */}
          <span className={`text-sm flex-1 truncate ${isActive ? 'font-medium' : ''}`}>
            {fileName} {isArchived ? '(archived)' : ''}
          </span>
        </div>
      </SortableItem>
    );
  };

  // Droppable Project Component
  const DroppableProject = ({ project }) => {
    const { setNodeRef, isOver } = useDroppable({
      id: `project-drop-${project.project_id}`,
      data: { type: 'project', project_id: project.project_id }
    });

    const isExpanded = expandedProjects.has(project.project_id);
    const projectFiles = project.files || [];
    const isArchived = project.archived || false;

    return (
      <div key={project.project_id} className="mb-1">
        <SortableItem id={project.project_id} type="project">
          <div
            ref={setNodeRef}
            className={`flex items-center py-1.5 px-3 hover:bg-gray-50/80 transition-colors duration-200 rounded-lg mx-2 group cursor-pointer ${
              isOver ? 'bg-blue-50 border-2 border-blue-300 border-dashed' : ''
            } ${isArchived ? 'opacity-50' : ''}`}
            onClick={() => toggleProject(project.project_id)}
            onContextMenu={(e) => handleContextMenu(e, 'project', project, isArchived)}
          >
            <button
              className="flex items-center justify-center w-4 h-4 mr-2 text-gray-400 hover:text-gray-600 flex-shrink-0"
              onClick={(e) => {
                e.stopPropagation();
                toggleProject(project.project_id);
              }}
            >
              {isExpanded ? <MdExpandMore size={16} /> : <MdChevronRight size={16} />}
            </button>

            <MdFolder
              size={16}
              className="text-yellow-600 mr-2 flex-shrink-0"
            />

            <span className="text-sm font-semibold text-gray-800 flex-1 truncate">
              {project.name} ({project.runtime_language}) {isArchived ? '(archived)' : ''}
            </span>

            <span className="text-xs text-gray-400 ml-2">({projectFiles.length})</span>
          </div>
        </SortableItem>

        {isExpanded && projectFiles.length > 0 && (
          <div className="ml-4">
            <SortableContext items={projectFiles.map(file => file.file.file_id)} strategy={verticalListSortingStrategy}>
              {projectFiles.map(fileItem => renderFile(fileItem, true))}
            </SortableContext>
          </div>
        )}
      </div>
    );
  };

  const handleDragStart = () => {
    // Could add visual feedback here if needed
  };

  const handleDragCancel = () => {
    // Could add cleanup here if needed
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    try {
      console.log('Drag end:', { active: active.id, over: over.id, overData: over.data?.current });

      // Check if we're dropping on a droppable zone
      if (over.id.toString().startsWith('project-drop-') || over.id === 'root-drop-zone') {
        const projectId = over.data?.current?.project_id || null;

        // Find the file being dragged
        const allFiles = [...files, ...projects.flatMap(p => p.files || [])];
        console.log('All files for searching:', allFiles.map(f => ({ id: f.file.file_id, name: f.file.name })));
        console.log('Looking for active id:', active.id);

        const draggedFile = allFiles.find(file => file.file.file_id === active.id);

        console.log('Found dragged file:', draggedFile);

        if (draggedFile && onMoveFileToProject) {
          console.log('Moving file to project:', projectId);
          await onMoveFileToProject(draggedFile.file.file_id, projectId);
        }
        return;
      }
    } catch (err) {
      console.error('Error during drag and drop:', err);
    }
  };

  // Context menu handlers
  const handleContextMenu = (e, type, target = null, isArchived = false) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({
      isOpen: true,
      position: { x: e.clientX, y: e.clientY },
      type,
      target,
      isArchived
    });
  };

  const closeContextMenu = () => {
    setContextMenu({
      isOpen: false,
      position: { x: 0, y: 0 },
      type: null,
      target: null
    });
  };

  // Filter projects and files based on archived status
  const filteredProjects = showArchived ? projects : projects.filter(p => !p.archived);
  const filteredFiles = showArchived ? files : files.filter(f => !f.archived);

  // Combine all items for sorting context - ensure proper ID mapping
  const allSortableIds = [
    ...filteredProjects.map(p => p.project_id),
    ...filteredFiles.map(f => f.file.file_id)
  ];

  // Context Menu Component
  const ContextMenu = () => {
    if (!contextMenu.isOpen) return null;

    return (
      <>
        {/* Backdrop */}
        <div
          className="fixed inset-0 z-40"
          onClick={closeContextMenu}
        />

        {/* Menu */}
        <div
          className="fixed bg-white border border-gray-200 rounded-lg shadow-lg py-2 z-50 min-w-48"
          style={{
            left: contextMenu.position.x,
            top: contextMenu.position.y,
          }}
        >
          {contextMenu.type === 'root' && (
            <>
              <button
                onClick={() => {
                  onShowFileModal(null);
                  closeContextMenu();
                }}
                className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center space-x-2"
              >
                <MdInsertDriveFile size={16} className="text-blue-500" />
                <span>New File</span>
              </button>
              <button
                onClick={() => {
                  onShowProjectModal();
                  closeContextMenu();
                }}
                className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center space-x-2"
              >
                <MdFolder size={16} className="text-yellow-600" />
                <span>New Project</span>
              </button>
            </>
          )}

          {contextMenu.type === 'project' && contextMenu.target && (
            <>
              <button
                onClick={() => {
                  onShowFileModal(contextMenu.target.project_id);
                  closeContextMenu();
                }}
                className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center space-x-2"
              >
                <MdInsertDriveFile size={16} className="text-blue-500" />
                <span>New File in {contextMenu.target.name}</span>
              </button>
              <div className="border-t border-gray-100 my-1" />
              {contextMenu.isArchived ? (
                <button
                  onClick={() => {
                    handleUnarchiveProject(contextMenu.target.project_id);
                    closeContextMenu();
                  }}
                  className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center space-x-2"
                >
                  <MdUnarchive size={16} className="text-green-600" />
                  <span>Unarchive Project</span>
                </button>
              ) : (
                <button
                  onClick={() => {
                    handleArchiveProject(contextMenu.target.project_id);
                    closeContextMenu();
                  }}
                  className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center space-x-2"
                >
                  <MdArchive size={16} className="text-orange-600" />
                  <span>Archive Project</span>
                </button>
              )}
              <div className="border-t border-gray-100 my-1" />
              <button
                onClick={() => {
                  onShowProjectModal();
                  closeContextMenu();
                }}
                className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center space-x-2"
              >
                <MdFolder size={16} className="text-yellow-600" />
                <span>New Project</span>
              </button>
            </>
          )}

          {contextMenu.type === 'file' && contextMenu.target && (
            <>
              {contextMenu.isArchived ? (
                <button
                  onClick={() => {
                    handleUnarchiveFile(contextMenu.target.file.file_id);
                    closeContextMenu();
                  }}
                  className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center space-x-2"
                >
                  <MdUnarchive size={16} className="text-green-600" />
                  <span>Unarchive File</span>
                </button>
              ) : (
                <button
                  onClick={() => {
                    handleArchiveFile(contextMenu.target.file.file_id);
                    closeContextMenu();
                  }}
                  className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center space-x-2"
                >
                  <MdArchive size={16} className="text-orange-600" />
                  <span>Archive File</span>
                </button>
              )}
            </>
          )}
        </div>
      </>
    );
  };

  return (
    <div className="h-full flex flex-col">
      {/* Explorer Header */}
      <div className="h-16 bg-white border-b border-gray-200/60 flex items-center justify-between px-4 flex-shrink-0">
        <h3 className="text-sm font-semibold text-gray-800">File Explorer</h3>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setShowArchived(!showArchived)}
            className={`flex items-center justify-center w-6 h-6 rounded transition-colors duration-200 ${
              showArchived
                ? 'text-blue-600 bg-blue-50 hover:bg-blue-100'
                : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50'
            }`}
            title={showArchived ? "Hide Archived" : "Show Archived"}
            disabled={loading}
          >
            <MdArchive size={14} />
          </button>
          <button
            onClick={() => onShowFileModal(null)}
            className="flex items-center justify-center w-6 h-6 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded transition-colors duration-200"
            title="New File"
            disabled={loading}
          >
            <MdAdd size={14} />
          </button>
          <button
            onClick={onShowProjectModal}
            className="flex items-center justify-center w-6 h-6 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded transition-colors duration-200"
            title="New Project"
            disabled={loading}
          >
            <MdFolder size={14} />
          </button>
        </div>
      </div>

      {/* File Tree */}
      <div className="flex-1 overflow-y-auto py-2 bg-gray-50/80">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          onDragStart={handleDragStart}
          onDragCancel={handleDragCancel}
          onDragEnd={handleDragEnd}
        >
          <div
            ref={setRootDropRef}
            className={`min-h-full ${isRootOver ? 'bg-blue-50/50' : ''}`}
            onContextMenu={(e) => handleContextMenu(e, 'root')}
            onClick={closeContextMenu}
          >
            {(filteredProjects.length === 0 && filteredFiles.length === 0) ? (
              <div className="p-6 text-center">
                <div className="mb-6">
                  <div className="w-16 h-16 mx-auto bg-gradient-to-br from-blue-100 to-indigo-200 rounded-xl flex items-center justify-center shadow-sm">
                    <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                    </svg>
                  </div>
                </div>

                <h3 className="text-lg font-semibold text-gray-900 mb-3">Code Files</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Create your first file to start coding in the editor.
                </p>

                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200/60 rounded-lg p-4">
                  <p className="text-xs text-blue-700 font-medium mb-1">Getting started:</p>
                  <div className="space-y-1 text-xs text-blue-600">
                    <div className="flex items-center space-x-2">
                      <div className="w-1.5 h-1.5 bg-blue-400 rounded-full"></div>
                      <span>Click the + icon to create a new file</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-1.5 h-1.5 bg-blue-400 rounded-full"></div>
                      <span>Use proper file extensions (.py, .js, .java, etc.)</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-1.5 h-1.5 bg-blue-400 rounded-full"></div>
                      <span>Files will be auto-saved as you type</span>
                    </div>
                  </div>
                </div>

                <button
                  onClick={() => onShowFileModal(null)}
                  className="flex items-center justify-center space-x-2 w-full px-4 py-2.5 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 transition-colors duration-200 mt-4"
                  disabled={loading}
                >
                  <span>Create File</span>
                  <MdInsertDriveFile size={16} />
                </button>
              </div>
            ) : (
              <SortableContext items={allSortableIds} strategy={verticalListSortingStrategy}>
                {/* Display Projects */}
                {filteredProjects.length > 0 && (
                  <div className="mb-4">
                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide px-4 mb-2">Projects</h4>
                    {filteredProjects.map((project) => (
                      <DroppableProject key={project.project_id} project={project} />
                    ))}
                  </div>
                )}

                {/* Display Standalone Files */}
                {filteredFiles.length > 0 && (
                  <div>
                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide px-4 mb-2">Standalone Files</h4>
                    {filteredFiles.map((fileItem) => renderFile(fileItem))}

                    {/* Drop zone indicator for root level */}
                    {isRootOver && filteredFiles.length > 0 && (
                      <div className="mx-2 mt-2 p-2 border-2 border-blue-300 border-dashed rounded-lg bg-blue-50/80">
                        <p className="text-xs text-blue-600 text-center">Drop here to move to standalone files</p>
                      </div>
                    )}
                  </div>
                )}
              </SortableContext>
            )}
          </div>
        </DndContext>
      </div>

      {/* Context Menu */}
      <ContextMenu />
    </div>
  );
};

export default FileExplorer;